import { useEffect, type ReactNode } from 'react';
import posthog from 'posthog-js';
import {
  PostHogErrorBoundary,
  PostHogFeature,
  PostHogProvider,
  useActiveFeatureFlags,
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
  usePostHog,
} from '@posthog/react';
import { useSession } from './session';

const POSTHOG_TOKEN = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as string | undefined;
/** Managed reverse proxy in production; first-party Worker path locally. */
const POSTHOG_API_HOST = import.meta.env.DEV
  ? '/relay'
  : (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://e.fldr.zip';
const POSTHOG_UI_HOST = 'https://us.posthog.com';

export const posthogEnabled = Boolean(POSTHOG_TOKEN);

export {
  PostHogErrorBoundary,
  PostHogFeature,
  useActiveFeatureFlags,
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
  usePostHog,
};

let initialized = false;

function ensurePostHog() {
  if (!POSTHOG_TOKEN || initialized) return;
  posthog.init(POSTHOG_TOKEN, {
    api_host: POSTHOG_API_HOST,
    ui_host: POSTHOG_UI_HOST,
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: true,
  });
  initialized = true;
}

/** Must render inside SessionProvider. */
export function PostHogIdentify() {
  const posthogClient = usePostHog();
  const { user } = useSession();

  useEffect(() => {
    if (!posthogClient) return;
    if (user) {
      posthogClient.identify(user.id, {
        email: user.email,
        demo: user.demo,
      });
      return;
    }
    posthogClient.reset();
  }, [posthogClient, user]);

  return null;
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  if (!posthogEnabled) return children;
  ensurePostHog();
  return (
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary
        fallback={
          <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', fontSize: 14 }}>
            Something went wrong. Reload and try again.
          </div>
        }
      >
        {children}
      </PostHogErrorBoundary>
    </PostHogProvider>
  );
}

export function resetAnalytics() {
  if (!posthogEnabled || !initialized) return;
  posthog.reset();
}

export function captureException(error: unknown, properties?: Record<string, unknown>) {
  if (!posthogEnabled || !initialized) return;
  posthog.captureException(error, properties);
}
